import { parseSelectValue } from '@/components/UI/DataTableV2/DataTable.util';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { CellValue, RangeType } from '@/support/hot.types';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import type Handsontable from 'handsontable';

export const isLookupFieldType = (fieldType?: string): boolean =>
  fieldType === 'lookup_select' || fieldType === 'lookup_multi_select';

export const readMultiSelectCell = (value?: string): Array<string> => parseSelectValue(value);

export const writeMultiSelectCell = (value: unknown): string =>
  Array.isArray(value) && _isNotEmpty(value) ? JSON.stringify(value) : '';

export const normalisePastedMultiSelect = (value: string): string => {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return value;
    }
    return JSON.stringify([String(parsed)]);
  } catch {
    return JSON.stringify(
      value
        .split(',')
        .map((entry) => entry.trim())
        .filter(_isNotEmpty)
    );
  }
};

export const multiSelectAfterPasteHandler = function (
  this: Handsontable.Core,
  data: Array<Array<CellValue>>,
  coords: Array<RangeType>
): void {
  if (_isEmpty(coords) || _isEmpty(data)) {
    return;
  }

  try {
    const cellUpdates: Array<[number, number, string]> = [];

    for (const { startRow, startCol, endRow, endCol } of coords) {
      const actualEndRow = Math.max(endRow, startRow + data.length - 1);
      for (let row = startRow; row <= actualEndRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const cellMeta = this.getCellMeta(row, col);

          if (
            cellMeta.type === MultiSelectEditor.type &&
            cellMeta.jsonEncoded === true &&
            cellMeta.isMultiSelect !== false
          ) {
            const dataRowIndex = (row - startRow) % data.length;
            const dataColIndex = (col - startCol) % (data[0]?.length ?? 1);
            const pastedValue = data[dataRowIndex]?.[dataColIndex];

            if (_isNil(pastedValue) || _isEmptyString(pastedValue)) {
              continue;
            }
            cellUpdates.push([row, col, normalisePastedMultiSelect(String(pastedValue))]);
          }
        }
      }
    }

    if (_isNotEmpty(cellUpdates)) {
      this.setDataAtCell(cellUpdates, 'edit');
      setTimeout(() => this.validateCells(), 100);
    }
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError('Error in multiselect paste handler', {
        slug: 'multiselect-paste-handler-error',
        cause: error,
        level: 'error',
      })
    );
  }
};
